/**
 * Sends the user to the login page, returning them to the *current page* once they re-authenticate.
 *
 * We deliberately use the current browser location for `next` rather than the URL of the request that failed: an
 * expired session is detected via a (potentially `/api/...`) XHR, and sending the user back to an API URL after login
 * is useless. Reloading the current page instead would loop, since the same request would fail again.
 */
const redirectToLogin = () => {
  const { pathname, search } = window.location;
  // Already on an AuthN page - don't bounce again (avoids a redirect loop).
  if (pathname.startsWith("/authn")) {
    return;
  }
  // Mirror the backend's staff/customer split (see core.routing.get_base_login_url).
  const loginPath = pathname.startsWith("/staff") ? "/authn/login-staff" : "/authn/login";
  const next = encodeURIComponent(pathname + search);
  window.location.assign(`${loginPath}?next=${next}`);
};

/**
 * Detects when the server bounced an XHR/API request to an HTML login page - i.e. axios transparently followed a 302
 * to `/authn/...` and resolved with the login *document* - and sends the user to login. Left alone, the caller would
 * try to parse that HTML as JSON.
 *
 * This only runs for *fulfilled* responses (it's wired into the response interceptor's success path). A genuine 401
 * rejects the axios promise and never reaches here, so 401s are intentionally handled by the calling code (e.g.
 * `useCustomerAppConfig` redirects to login; brokers renders a "session expired" page) - that's what lets each app
 * decide what a 401 means rather than forcing a global redirect.
 *
 * @param response - A fulfilled axios response object
 * @throws Error if the server redirected the request to the login page
 */
export const handleAuthenticationFailure = (response: any) => {
  // Check if server redirected API request to login page (HTML response)
  const isHtmlResponse = response?.headers?.["content-type"]?.includes?.("text/html");

  if (isHtmlResponse) {
    const { pathname } = response.request?.responseURL ? new URL(response.request.responseURL) : { pathname: "" };

    if (pathname.startsWith("/authn/")) {
      redirectToLogin();
      throw new Error("Server redirected API request to login page, most likely due to expired session");
    }
  }
};
